import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Search, X, ShoppingCart, Zap, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('gallery') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/archive', label: 'Archive' },
  { to: '/add-to-homescreen', label: 'Add to Home Screen' },
  { to: '/admin/login', label: 'Admin' },
];

const FOOTER_LEGAL_LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
];

// Quick Buy Card Component with hover popup
function PrintCard({ print, pricing, typeNames, onOpenFullModal, onQuickAddToCart }) {
  const [showQuickBuy, setShowQuickBuy] = useState(false);
  const [quickType, setQuickType] = useState('paper');
  const [quickSize, setQuickSize] = useState('');
  const cardRef = useRef(null);
  const timeoutRef = useRef(null);

  // Popular sizes for quick buy (subset of full options)
  const getQuickSizes = (type) => {
    if (!pricing[type]) return [];
    const allSizes = Object.entries(pricing[type]);
    // Show first 3 most affordable sizes for quick selection
    return allSizes.slice(0, 3);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setShowQuickBuy(true);
      // Auto-select first size when popup appears
      if (pricing[quickType]) {
        setQuickSize(Object.keys(pricing[quickType])[0]);
      }
    }, 300);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setShowQuickBuy(false);
    }, 200);
  };

  const handleQuickTypeChange = (type) => {
    setQuickType(type);
    if (pricing[type]) {
      setQuickSize(Object.keys(pricing[type])[0]);
    }
  };

  const handleQuickAddToCart = (e) => {
    e.stopPropagation();
    if (!quickSize) return;
    onQuickAddToCart(print, quickType, quickSize, pricing[quickType][quickSize]);
  };

  const quickSizes = getQuickSizes(quickType);
  const currentPrice = pricing[quickType]?.[quickSize] || 0;

  return (
    <div 
      ref={cardRef}
      id={`print-${print.id}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        background: '#fff',
        borderRadius: '8px',
        overflow: 'visible',
        boxShadow: showQuickBuy ? '0 8px 24px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
        cursor: 'pointer',
        position: 'relative',
        transition: 'transform 0.2s, box-shadow 0.2s',
        transform: showQuickBuy ? 'translateY(-4px)' : 'none',
        zIndex: showQuickBuy ? 10 : 1
      }}
      data-testid={`print-card-${print.id}`}
    >
      {/* Featured Badge */}
      {print.featured && (
        <span style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          background: '#ffd700',
          color: '#333',
          padding: '3px 8px',
          borderRadius: '10px',
          fontSize: '11px',
          fontWeight: '600',
          zIndex: 2
        }}>★ Featured</span>
      )}
      
      {/* Image - clicks open full modal */}
      <div 
        onClick={() => onOpenFullModal(print)}
        onContextMenu={(e) => e.preventDefault()}
      >
        <img 
          src={print.image_url} 
          alt={print.title} 
          style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block', borderRadius: '8px 8px 0 0' }}
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        />
      </div>
      
      {/* Title bar */}
      <div 
        onClick={() => onOpenFullModal(print)}
        style={{ padding: '10px 12px', borderBottom: showQuickBuy ? '1px solid #eee' : 'none' }}
      >
        <h3 style={{ 
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: '14px',
          margin: '0 0 4px',
          color: '#1a1a1a',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>{print.title}</h3>
        <span style={{ fontSize: '11px', color: '#888' }}>
          {showQuickBuy ? 'Quick buy below ↓' : 'Hover for quick buy'}
        </span>
      </div>

      {/* Quick Buy Popup */}
      {showQuickBuy && (
        <div 
          style={{
            padding: '12px',
            background: '#fff',
            borderRadius: '0 0 8px 8px',
            animation: 'fadeIn 0.2s ease'
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="quick-buy-popup"
        >
          {/* Quick Type Selector */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', fontWeight: '600' }}>Type</div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['paper', 'canvas', 'metal'].map(type => (
                <button
                  key={type}
                  onClick={() => handleQuickTypeChange(type)}
                  style={{
                    flex: 1,
                    padding: '6px 4px',
                    fontSize: '10px',
                    fontWeight: '500',
                    border: '1px solid',
                    borderColor: quickType === type ? '#1a1a1a' : '#ddd',
                    background: quickType === type ? '#1a1a1a' : '#fff',
                    color: quickType === type ? '#fff' : '#666',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                  data-testid={`quick-type-${type}`}
                >
                  {type === 'paper' ? 'Paper' : type === 'canvas' ? 'Canvas' : 'Metal'}
                </button>
              ))}
            </div>
          </div>

          {/* Quick Size Selector */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px', fontWeight: '600' }}>Size</div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {quickSizes.map(([size, price]) => (
                <button
                  key={size}
                  onClick={() => setQuickSize(size)}
                  style={{
                    flex: 1,
                    padding: '6px 4px',
                    fontSize: '10px',
                    border: '1px solid',
                    borderColor: quickSize === size ? '#1a1a1a' : '#ddd',
                    background: quickSize === size ? '#1a1a1a' : '#fff',
                    color: quickSize === size ? '#fff' : '#666',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.15s'
                  }}
                  data-testid={`quick-size-${size}`}
                >
                  <div style={{ fontWeight: '600' }}>{size}</div>
                  <div style={{ opacity: 0.8 }}>${price}</div>
                </button>
              ))}
            </div>
            <button
              onClick={() => onOpenFullModal(print)}
              style={{
                width: '100%',
                marginTop: '6px',
                padding: '4px',
                fontSize: '10px',
                color: '#666',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              More sizes →
            </button>
          </div>

          {/* Quick Add to Cart Button */}
          <button
            onClick={handleQuickAddToCart}
            disabled={!quickSize}
            style={{
              width: '100%',
              padding: '10px',
              fontSize: '13px',
              fontWeight: '600',
              background: '#1a1a1a',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'background 0.2s'
            }}
            data-testid="quick-buy-btn"
          >
            <Zap size={14} />
            {`Add to Cart · $${currentPrice}`}
          </button>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// Category display order
const CATEGORY_ORDER = [
  'Featured Images',
  'Best Selling',
  '2027 Calendar',
  'Beachscapes',
  'Digital Art',
  'Dodging Shadows',
  'Flora & Fauna',
  'Landmarks',
  'Northeaster',
  'OBX Sunrise',
  'Pelicans',
  'Ripples',
  'Waves',
];

// Turns a category name into a safe, stable DOM id for deep-linking (e.g. "Flora & Fauna" -> "flora-fauna").
function categorySlug(name) {
  return `gallery-category-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`;
}

// Drives which layout a category renders — a grid on desktop, a
// swipeable single-photo carousel on phones — without mounting (and
// double-downloading the images for) both at once.
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}

// One swipeable slide in the mobile category carousel — a simpler stand-in
// for PrintCard, since the hover quick-buy popup that card offers has no
// equivalent on touch; tapping the photo opens the same full print modal.
function CarouselSlide({ print, onOpenFullModal }) {
  return (
    <div
      onClick={() => onOpenFullModal(print)}
      style={{ flex: '0 0 85%', width: '85%', maxWidth: '85%', scrollSnapAlign: 'center', cursor: 'pointer' }}
    >
      <div style={{ width: '100%' }} onContextMenu={(e) => e.preventDefault()}>
        <img
          src={print.image_url}
          alt={print.title}
          style={{ width: '100%', aspectRatio: '4 / 3', maxHeight: '420px', objectFit: 'cover', borderRadius: '12px', display: 'block' }}
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        />
      </div>
      <h3 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '16px',
        margin: '10px 4px 0',
        color: '#1a1a1a',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>{print.title}</h3>
    </div>
  );
}

function Gallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [prints, setPrints] = useState([]);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState({});
  const [typeNames, setTypeNames] = useState({});
  const [searchQuery, setSearchQuery] = useState(searchParams.get('tag') || '');
  const [selectedPrint, setSelectedPrint] = useState(null);
  const [selectedType, setSelectedType] = useState('paper');
  const [selectedSize, setSelectedSize] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const { addItem, itemCount, openCart } = useCart();
  const isMobile = useIsMobile();

  // Locking background scroll while the print modal is open avoids a known
  // mobile Chrome/Android quirk where a `position: fixed` overlay opened
  // over a still-scrollable page renders anchored to the scroll position
  // it opened at, instead of the actual viewport — requiring the user to
  // scroll around to find it.
  useEffect(() => {
    if (!selectedPrint) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [selectedPrint]);

  // Load recently viewed from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('recentlyViewedPrints');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setRecentlyViewed(parsed);
      } catch (e) {
        console.error('Failed to parse recently viewed:', e);
      }
    }
  }, []);

  // Helper to add a print to recently viewed
  const addToRecentlyViewed = (print) => {
    const MAX_RECENT = 6;
    setRecentlyViewed(prev => {
      // Remove if already exists (to move to front)
      const filtered = prev.filter(p => p.id !== print.id);
      // Add to front, limit to MAX_RECENT
      const updated = [
        { id: print.id, title: print.title, image_url: print.image_url },
        ...filtered
      ].slice(0, MAX_RECENT);
      // Save to localStorage
      localStorage.setItem('recentlyViewedPrints', JSON.stringify(updated));
      return updated;
    });
  };

  useEffect(() => {
    fetchGallery();
    fetchPricing();
    fetchTags();
    fetchCategories();
  }, []);

  useEffect(() => {
    // If coming from a post linking to a specific category section, scroll to it
    const category = searchParams.get('category');
    if (category && prints.length > 0) {
      const element = document.getElementById(categorySlug(category));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [searchParams, prints]);

  // Which category section is on screen, for the sticky "jump to" bar.
  const [activeCategory, setActiveCategory] = useState('');
  const jumpBarRef = useRef(null);

  useEffect(() => {
    if (loading || prints.length === 0) return;
    const onScroll = () => {
      const barHeight = jumpBarRef.current ? jumpBarRef.current.offsetHeight : 0;
      const sections = document.querySelectorAll('[data-gallery-category]');
      let current = '';
      sections.forEach(section => {
        if (section.getBoundingClientRect().top <= barHeight + 40) {
          current = section.getAttribute('data-gallery-category');
        }
      });
      setActiveCategory(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [loading, prints]);

  useEffect(() => {
    // Keep the highlighted chip visible when the bar is wider than the screen.
    const bar = jumpBarRef.current;
    const chip = bar && activeCategory
      ? bar.querySelector(`[data-jump="${CSS.escape(activeCategory)}"]`)
      : null;
    if (chip) {
      bar.scrollTo({ left: chip.offsetLeft - (bar.clientWidth - chip.offsetWidth) / 2, behavior: 'smooth' });
    }
  }, [activeCategory]);

  const jumpToCategory = (category) => {
    const element = document.getElementById(categorySlug(category));
    if (!element) return;
    const barHeight = jumpBarRef.current ? jumpBarRef.current.offsetHeight : 0;
    const top = element.getBoundingClientRect().top + window.scrollY - barHeight - 12;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  useEffect(() => {
    // A ?print=<id> link (from a post or the modal's Share button) opens that
    // print's modal directly. Doesn't depend on the card being in the DOM —
    // the mobile carousel doesn't render the print-<id> anchors. Waits for
    // pricing so the type/size selection initializes properly.
    const printId = searchParams.get('print');
    if (!printId || prints.length === 0 || Object.keys(pricing).length === 0) return;
    if (selectedPrint?.id === printId) return;
    const print = prints.find(p => p.id === printId);
    if (!print) return;
    document.getElementById(`print-${printId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    openPrintModal(print);
  }, [searchParams, prints, pricing]);

  const fetchGallery = async (tagOverride) => {
    try {
      const tag = tagOverride !== undefined ? tagOverride : (searchParams.get('tag') || '');
      const response = await axios.get(`${BACKEND_URL}/api/public/gallery${tag ? `?tag=${encodeURIComponent(tag)}` : ''}`);
      setPrints(response.data);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/gallery/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Group prints by category
  const groupedPrints = () => {
    const groups = {};
    
    prints.forEach(print => {
      // A post's photos go under whichever category the post chose; posts
      // that never set one fall back to "Featured Images" (the old default).
      const category = print.category || (print.source === 'post' ? 'Featured Images' : 'Uncategorized');
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(print);
    });
    
    // Sort categories by predefined order
    const sortedCategories = Object.keys(groups).sort((a, b) => {
      const indexA = CATEGORY_ORDER.indexOf(a);
      const indexB = CATEGORY_ORDER.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
    
    return { groups, sortedCategories };
  };

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/prints/pricing`);
      setPricing(response.data.pricing);
      setTypeNames(response.data.type_names);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/gallery/tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams(searchQuery ? { tag: searchQuery } : {});
    fetchGallery(searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchParams({});
    fetchGallery('');
  };

  const openPrintModal = (print) => {
    setSelectedPrint(print);
    const availableTypes = print.available_types || ['paper', 'canvas', 'metal'];
    setSelectedType(availableTypes[0]);
    if (pricing[availableTypes[0]]) {
      setSelectedSize(Object.keys(pricing[availableTypes[0]])[0]);
    }
    setSpecialInstructions('');
    // Track in recently viewed
    addToRecentlyViewed(print);
    // Put the print in the address bar so the URL is shareable as-is.
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('print', print.id);
      next.delete('category');
      return next;
    }, { replace: true });
  };

  const closePrintModal = () => {
    setSelectedPrint(null);
    setSpecialInstructions('');
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('print');
      return next;
    }, { replace: true });
  };

  const sharePrint = async (print) => {
    const url = `${window.location.origin}/gallery?print=${encodeURIComponent(print.id)}`;
    // Phones get the native share sheet (Messages, Facebook, etc.); desktop
    // browsers without it just copy the link.
    if (navigator.share) {
      try {
        await navigator.share({ title: print.title, url });
      } catch (e) {
        // User closed the share sheet — nothing to do.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied — paste it anywhere to share this photo');
    } catch (e) {
      toast.error('Could not copy the link');
    }
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    if (pricing[type]) {
      setSelectedSize(Object.keys(pricing[type])[0]);
    }
  };

  const getPrice = () => {
    if (pricing[selectedType] && pricing[selectedType][selectedSize]) {
      return pricing[selectedType][selectedSize];
    }
    return 0;
  };

  const handleAddToCart = () => {
    if (!selectedPrint || !selectedType || !selectedSize) {
      toast.error('Please select print options');
      return;
    }
    addItem({
      shop: 'gallery',
      product_id: selectedPrint.id,
      product_title: selectedPrint.title,
      image_url: selectedPrint.image_url,
      variant: `${selectedType}-${selectedSize}`,
      variant_label: `${typeNames[selectedType] || selectedType} - ${selectedSize}`,
      price: getPrice(),
      quantity: 1,
      special_instructions: specialInstructions || null
    });
    toast.success(`${selectedPrint.title} added to cart!`);
    closePrintModal();
  };

  const handleQuickAddToCart = (print, printType, size, price) => {
    addItem({
      shop: 'gallery',
      product_id: print.id,
      product_title: print.title,
      image_url: print.image_url,
      variant: `${printType}-${size}`,
      variant_label: `${typeNames[printType] || printType} - ${size}`,
      price: price,
      quantity: 1,
      special_instructions: null
    });
    toast.success(`${print.title} added to cart!`);
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} cart={{ itemCount, onClick: openCart }} />

      {/* Gallery Hero */}
      <section className="gallery-hero" style={{ textAlign: 'center', padding: '60px 20px 30px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img 
          src="https://daily-obx-cdn.b-cdn.net/newsletter-images/bbg-logo.jpg" 
          alt="Beach Bum Gallery - Mostly Good Beach Photography" 
          className="shop-hero-logo"
          style={{ maxWidth: '500px', marginBottom: '15px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
        />
        <p style={{ fontSize: '18px', color: '#666', margin: 0, textAlign: 'center' }}>Fine art prints of stunning OBX photography</p>
      </section>

      {/* Search/Filter */}
      <div className="gallery-filter" style={{ maxWidth: '600px', margin: '0 auto', padding: '30px 20px', textAlign: 'center' }}>
        <form onSubmit={handleSearch} className="search-form" style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <div className="search-input-wrapper">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search prints..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" onClick={clearSearch} className="clear-search">
                <X size={18} />
              </button>
            )}
          </div>
          <Button type="submit">Search</Button>
        </form>
        
        {tags.length > 0 && (
          <div className="tag-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '20px', justifyContent: 'center' }}>
            {tags.slice(0, 10).map(tag => (
              <button
                key={tag}
                onClick={() => { setSearchQuery(tag); setSearchParams({ tag }); fetchGallery(tag); }}
                className={`tag-pill ${searchQuery === tag ? 'active' : ''}`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Recently Viewed Section */}
      {recentlyViewed.length > 0 && !loading && (
        <div style={{ 
          maxWidth: '1400px', 
          margin: '0 auto', 
          padding: '20px 20px 0',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #f8f9fa 0%, #fff 100%)',
            borderRadius: '12px',
            padding: '20px 24px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            border: '1px solid #eee'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h3 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: '18px',
                fontWeight: '600',
                color: '#1a1a1a',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>◷</span>
                Recently Viewed
              </h3>
              <button
                onClick={() => {
                  localStorage.removeItem('recentlyViewedPrints');
                  setRecentlyViewed([]);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#888',
                  fontSize: '12px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'color 0.2s'
                }}
                data-testid="clear-recent-btn"
              >
                Clear
              </button>
            </div>
            <div style={{ 
              display: 'flex', 
              gap: '12px',
              overflowX: 'auto',
              paddingBottom: '8px'
            }}>
              {recentlyViewed.map((item) => {
                // Find full print data if available
                const fullPrint = prints.find(p => p.id === item.id) || item;
                return (
                  <div
                    key={item.id}
                    onClick={() => openPrintModal(fullPrint)}
                    onContextMenu={(e) => e.preventDefault()}
                    style={{
                      flex: '0 0 auto',
                      width: '140px',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      background: '#fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    data-testid={`recent-item-${item.id}`}
                  >
                    <img 
                      src={item.image_url} 
                      alt={item.title}
                      style={{
                        width: '100%',
                        height: '90px',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                      draggable="false"
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                    />
                    <div style={{ padding: '8px 10px' }}>
                      <p style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#1a1a1a',
                        margin: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>{item.title}</p>
                      <span style={{ 
                        fontSize: '10px', 
                        color: '#666' 
                      }}>View again →</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Gallery Grid */}
      <main className="gallery-main" style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
        {loading ? (
          <div className="loading-spinner"></div>
        ) : prints.length === 0 ? (
          <div className="empty-gallery">
            <h3>No prints found</h3>
            <p>Try a different search term or check back soon for new additions.</p>
          </div>
        ) : (
          <div>
            {(() => {
              const { groups, sortedCategories } = groupedPrints();
              return (
                <>
                {sortedCategories.length > 1 && (
                  <nav className="gallery-jump" ref={jumpBarRef} aria-label="Gallery categories" data-testid="gallery-jump">
                    {sortedCategories.map(category => (
                      <button
                        key={category}
                        type="button"
                        data-jump={category}
                        className={`gallery-jump-chip ${activeCategory === category ? 'active' : ''}`}
                        onClick={() => jumpToCategory(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </nav>
                )}
                {sortedCategories.map(category => (
                <div key={category} id={categorySlug(category)} data-gallery-category={category} style={{ marginBottom: '50px' }}>
                  <h2 style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontSize: '28px',
                    fontWeight: '600',
                    marginBottom: '20px',
                    paddingBottom: '10px',
                    borderBottom: '2px solid #e0e0e0',
                    color: '#1a1a1a'
                  }}>
                    {category}
                  </h2>
                  {isMobile ? (
                    <div style={{
                      display: 'flex',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      scrollSnapType: 'x mandatory',
                      WebkitOverflowScrolling: 'touch',
                      gap: '12px',
                      margin: '0 -20px',
                      padding: '0 20px 8px',
                      boxSizing: 'border-box'
                    }}>
                      {groups[category].map((print) => (
                        <CarouselSlide key={print.id} print={print} onOpenFullModal={openPrintModal} />
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                      gap: '16px',
                      width: '100%'
                    }}>
                      {groups[category].map((print) => (
                        <PrintCard
                          key={print.id}
                          print={print}
                          pricing={pricing}
                          typeNames={typeNames}
                          onOpenFullModal={openPrintModal}
                          onQuickAddToCart={handleQuickAddToCart}
                        />
                      ))}
                    </div>
                  )}
                </div>
                ))}
                </>
              );
            })()}
          </div>
        )}
      </main>

      {/* Print Modal - rendered via portal to document.body */}
      {selectedPrint && createPortal(
        <div 
          className="print-modal-overlay" 
          onClick={closePrintModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '20px',
            boxSizing: 'border-box'
          }}
        >
          <div className="print-modal" onClick={e => e.stopPropagation()} style={{
            background: '#fff',
            borderRadius: '16px',
            width: '95%',
            maxWidth: '1400px',
            minWidth: 0,
            height: 'min(800px, 90vh)',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
          }}>
            <button className="modal-close" onClick={closePrintModal}>
              <X size={24} />
            </button>
            
            <div className="modal-content" style={{
              display: 'flex',
              flexDirection: 'row',
              height: '100%'
            }}>
              <div
                className="modal-image"
                style={{
                  flex: '0 0 65%',
                  width: '65%',
                  minWidth: 0,
                  background: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '24px',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  position: 'relative'
                }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <img 
                  src={selectedPrint.image_url} 
                  alt={selectedPrint.title} 
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                  draggable="false"
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                />
                {/* Invisible overlay to prevent right-click save */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'transparent'
                }} />
              </div>
              
              <div className="modal-details" style={{
                flex: '0 0 35%',
                width: '35%',
                minWidth: 0,
                padding: '30px',
                background: '#fff',
                overflowY: 'auto',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}>
                <h2>{selectedPrint.title}</h2>
                <button
                  onClick={() => sharePrint(selectedPrint)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 14px', marginBottom: '16px',
                    border: '1px solid #ddd', borderRadius: '20px',
                    background: '#fff', color: '#333', fontSize: '14px', cursor: 'pointer'
                  }}
                >
                  <Share2 size={15} /> Share this photo
                </button>
                {selectedPrint.description && (
                  <p className="print-description">{selectedPrint.description}</p>
                )}
                
                {/* Print Type Selector */}
                <div className="option-group">
                  <label style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', display: 'block' }}>Print Type</label>
                  <div className="type-buttons" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {(selectedPrint.available_types || ['paper', 'canvas', 'metal']).map(type => (
                      <button
                        key={type}
                        className={`type-btn ${selectedType === type ? 'active' : ''}`}
                        onClick={() => handleTypeChange(type)}
                        style={{
                          padding: '12px 20px',
                          border: '2px solid',
                          borderColor: selectedType === type ? '#1a1a1a' : '#ddd',
                          background: selectedType === type ? '#1a1a1a' : '#fff',
                          color: selectedType === type ? '#fff' : '#333',
                          borderRadius: '8px',
                          fontSize: '16px',
                          cursor: 'pointer'
                        }}
                      >
                        {typeNames[type] || type}
                      </button>
                    ))}
                    <button 
                      className="type-btn disabled" 
                      disabled
                      style={{
                        padding: '12px 20px',
                        border: '2px solid #eee',
                        background: '#f5f5f5',
                        color: '#999',
                        borderRadius: '8px',
                        fontSize: '16px',
                        cursor: 'not-allowed'
                      }}
                    >
                      Framed Prints - Coming Soon
                    </button>
                  </div>
                </div>

                {/* Size Selector */}
                <div className="option-group">
                  <label style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', display: 'block' }}>Size</label>
                  <div className="size-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
                    {pricing[selectedType] && Object.entries(pricing[selectedType]).map(([size, price]) => (
                      <button
                        key={size}
                        className={`size-btn ${selectedSize === size ? 'active' : ''}`}
                        onClick={() => setSelectedSize(size)}
                        style={{
                          padding: '14px',
                          border: '2px solid',
                          borderColor: selectedSize === size ? '#1a1a1a' : '#ddd',
                          background: selectedSize === size ? '#1a1a1a' : '#fff',
                          color: selectedSize === size ? '#fff' : '#333',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'center'
                        }}
                      >
                        <span style={{ display: 'block', fontWeight: '600', fontSize: '16px' }}>{size}</span>
                        <span style={{ display: 'block', fontSize: '15px', marginTop: '4px', opacity: 0.8 }}>${price}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Special Instructions */}
                <div className="option-group">
                  <label style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', display: 'block' }}>Special Instructions or Personal Note (optional)</label>
                  <textarea
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Gift message, special requests, etc."
                    rows={3}
                    style={{ width: '100%', padding: '14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </div>

                {/* Price & Checkout */}
                <div className="checkout-section">
                  <div className="price-display">
                    <span className="price-label">Total:</span>
                    <span className="price-amount">${getPrice()}</span>
                  </div>
                  <Button
                    className="checkout-btn"
                    onClick={handleAddToCart}
                    disabled={!selectedSize}
                  >
                    <ShoppingCart size={20} />
                    Add to Cart
                  </Button>
                </div>

                <p className="shipping-note">
                  Shipping calculated at checkout. US shipping only.
                </p>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Footer */}
      <Footer links={FOOTER_LINKS} legalLinks={FOOTER_LEGAL_LINKS} />
    </div>
  );
}

export default Gallery;
